import {
    Component,
    OnChanges,
    OnDestroy,
    ViewChild,
    Input,
    ElementRef,
    OnInit,
    NgZone,
    Output,
    EventEmitter
} from '@angular/core';
import { CurrencyPipe, DecimalPipe, DatePipe } from '@angular/common';

import { throwError, of, combineLatest, Subject } from 'rxjs';
import { filter, map, catchError, take, takeUntil } from 'rxjs/operators';
import * as am4core from '@amcharts/amcharts4/core';
import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';

import { DataPointModel } from '../../models/data-point.model';
import { SeriesModel } from '../../models/series.model';
import { SignificantDigitsPipe } from '../../../Misc/pipes/SignificantDigits/significant-digits.pipe';
import { printHelper } from '../../../Misc/helpers/print/print.helper';
import { download } from '../../../Misc/helpers/download/download.helper';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { AgprMath } from '../../../Misc/helpers/math/math-helper';
import { DataListChartOptionsModel } from '../../models/data-list-chart-options.model';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';


@Component({
    selector: 'agpr-data-list-chart',
    templateUrl: './data-list-chart.component.html',
    styleUrls: ['./data-list-chart.component.scss'],
    standalone: false
})
export class DataListChartComponent implements OnChanges, OnInit, OnDestroy {

    @Input() series: SeriesModel[];
    @Input() skinny: boolean;
    @Input() colors: { [key: string]: string };
    @Input() chartName: string;
    @Input() responsive: boolean = false;
    @Input() chartOptions: DataListChartOptionsModel = {
        defaultColors: userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors,
        currencyCode: UserCurrency.USD
    };
    @Input() block: BlockModel;
    @Input() hideTooltip: boolean = false;
    @Input() forceBlockHeight: number;
    @Input() hasHeader: number = 0;
    @Input() id: string;

    @Output() dataChange = new EventEmitter<boolean>();
    @Output() sourceDeleted = new EventEmitter<boolean>();

    public data: DataPointModel[];
    public previousPeriodData: DataPointModel[];
    public loading: boolean = false;
    public error: Error;

    protected oldSeries: SeriesModel[];

    private watchDestroy$ = new Subject<void>();

    readonly exportFormats: (keyof am4core.IExportOptions)[] = [
        'png',
        'jpg',
        'svg',
        'csv',
        'json',
        'print'
    ];

    @ViewChild('chartContainer') chartContainer: ElementRef;

    constructor(
        public elementRef: ElementRef,
        public decimalPipe: DecimalPipe,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public significantDigitsPipe: SignificantDigitsPipe,
        // do not inject localizationService or other user/account specific services in this model
        // until we develop a solution to deal with Localization in shareable report repo
        // public localizationService: LocalizationService,
        public zone: NgZone,
    ) {}

    ngOnInit(): void {
        const rsm = this.series[0] as ReportSeriesModel;
        if (rsm?.styles?.currency) {
            this.chartOptions.currencyCode = rsm.styles.currency;
        }
    }

    ngOnDestroy(): void {
        // Final cleanup
        this.watchDestroy$.next();
        this.watchDestroy$.complete();
    }

    ngOnChanges(): void {
        if (this.series !== this.oldSeries) {
            this.oldSeries = this.series;
            // Cancel previous subscriptions
            this.watchDestroy$.next();
            if (this.series && this.series.length) {
                this.watchData().subscribe({
                    next: () => {
                        requestAnimationFrame(() => {
                            this.dataChange.emit(true);
                        });
                    }
                });
            }
        }
    }

    /**
     * Updates the current presentation of data in the chart
     */
    updateData(data: DataPointModel[], previousPeriod?: boolean) {
        if (previousPeriod) {
            this.previousPeriodData = data;
        } else {
            this.data = data;
        }

        // since previousPeriod calculations rely on currentPeriod, only perform calculations once dependents are ready
        if (this.data?.length && this.previousPeriodData?.length) {
            for (const slice of this.data) {
                const previousPeriodValue = this.previousPeriodData.find(x => x.id === slice.id);
                if (!previousPeriodValue) {
                    continue;
                }

                // calculate the delta percentage between previous and current period
                const xValuePreviousPeriod = previousPeriodValue.xValue as number;
                slice.xValuePreviousPeriod = xValuePreviousPeriod;
                slice.xValueDeltaPercent = this.percentageChange(slice.xValue, xValuePreviousPeriod) ?? 0;

                slice.previousPeriodTooltip = this.hideTooltip ? '' : this.getPreviousPeriodTooltip(slice.label, slice.xValueDeltaPercent);
            }
        }
    }

    /**
     * Returns the percentage change between two numbers
     * Will return -Infinity when current data is 0
     * will return Infinity if previous data is 0
     * Will return is undefined is current & prev data is 0
     */
    public percentageChange (currentData: number, previousData: number, decimalPlaces: number = 1): number {
        if (currentData === 0) {
            // if both currentData & previousData are zero, we want to return undefined (aka current & previous data is the same)
            if (previousData === 0) {
                return undefined;
            }

            // if currentData is zero, we want to return negative infinity, this is being used in the coverage summary chart
            return -Infinity;
        }
        // if previousData is zero, we want to return infinity, this is being used in the coverage summary chart
        if (previousData === 0) {
            return Infinity;
        }
        // When dealing with numbers with different signs measures the % change after you cross the zero line,
        // as the % change before you cross the zero line is always 100% ... spooky
        return AgprMath.round(100 * ((currentData - previousData) / Math.abs(previousData)), decimalPlaces);
    };


    /**
     * Exports the chart data
     */
    public async exportData(type: keyof am4core.IExportOptions) {
        if (!this.chartContainer) {
            return;
        }

        let url: string;
        let blob: Blob;

        this.elementRef.nativeElement.children[0].setAttribute('style', 'background-color: white; padding: 5px 0 0 5px;');
        switch (type) {
            case 'jpg':
                url = await htmlToImage.toJpeg(this.elementRef.nativeElement.children[0]);
                break;

            case 'png':
                url = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);
                break;

            case 'svg':
                url = await htmlToImage.toSvg(this.elementRef.nativeElement.children[0]);
                break;

            case 'csv':
                const csvLabels = [];
                const csvValues = [];
                const csvData = [
                    csvLabels,
                    csvValues
                ];

                this.data.forEach((item) => {
                    const label = item.csvLabel || item.label;
                    let value = '' + item.xValue;

                    if (item.currency) {
                        value = this.currencyPipe.transform(value, <string>item.currency);

                    } else if (item.numberFormat) {
                        value = this.decimalPipe.transform(value, <string>item.numberFormat);

                    } else if (item.dateFormat) {
                        value = this.datePipe.transform(value, <string>item.dateFormat);
                    }

                    csvLabels.push(label);
                    csvValues.push(value);
                });

                const csvString: string = papaparse.unparse(csvData, {delimiter: ','});

                blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                break;

            case 'json':
                const jsonData = {};
                this.data.forEach((item) => {
                    jsonData[(item.jsonLabel || item.label) as string] = item.xValue;
                });

                blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                break;

            case 'print':
                const printElement = this.elementRef.nativeElement.children[0];
                const printData = await htmlToImage.toSvg(printElement);
                printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                break;
        }

        this.elementRef.nativeElement.children[0].setAttribute('style', 'background-color: unset; padding: 0;');

        if (blob) {
            download.blob(blob, `${this.chartName}.${type}`);
        } else if (url) {
            download.url(url, `${this.chartName}.${type}`);
        }
    }

    private watchData() {
        this.loading = true;

        const series$ = this.series.map(item => {
            const previousPeriodDataProvider$ = (item.hasPreviousPeriod) ? item.previousPeriodDataProvider
                .pipe(
                    filter(v => !!v),
                    takeUntil(this.watchDestroy$),
                    map((data) => {
                        this.zone.runOutsideAngular(() => {
                            this.updateData(data, true);
                            this.loading = false;
                        });
                    }),
                    catchError((error) => {
                        this.data = [];
                        this.error = error;
                        this.loading = false;

                        return throwError(() => new Error(error));
                    })
                ) : of([]);

            const currentPeriodDataProvider$ = item.dataProvider.pipe(
                filter(v => !!v),
                takeUntil(this.watchDestroy$),
                map((data) => {
                    this.zone.runOutsideAngular(() => {
                        this.updateData(data, false);
                        this.loading = false;
                    });
                }),
                catchError((error) => {
                    this.data = [];
                    this.error = error;
                    this.loading = false;
                    if (error.message === 'Data source no longer available') {
                        this.sourceDeleted.emit(true);
                    }

                    return throwError(() => new Error(error));
                })
            );

            return combineLatest([
                currentPeriodDataProvider$,
                previousPeriodDataProvider$
            ]);
        });

        return combineLatest(series$);
    }

    private getPreviousPeriodTooltip(heading: string, percent: number): string {
        if (!this.chartOptions) {
            return '';
        }
        let tooltip = this.chartOptions.neutralTooltip;

        if (percent > 0) {
            tooltip = this.chartOptions.positiveTooltip;
        } else if (percent < 0) {
            tooltip = this.chartOptions.negativeTooltip;
        }

        return tooltip.replace('{{metric}}', heading).replace('{{value}}', Math.abs(percent) + '');
    }
}
